import { arrayExclusive } from "./arrayExclusive";

const TypedArrays = [
  Uint8Array,
  Uint16Array,
  Uint32Array,
  BigUint64Array,
  Uint8ClampedArray,
  Int8Array,
  Int16Array,
  Int32Array,
  BigInt64Array,
  Float32Array,
  Float64Array,
];

const hasOwn = Object.prototype.hasOwnProperty;

/** Iterate every value in an entire object tree (if just a scalar, the one value is yielded).
 * In recursive trees, only yields object values and their descendants once. */
export function* deepIterate(value: unknown): Generator<unknown> {
  const seen = new Set<object>();

  function* iterate(value: unknown): Generator<unknown> {
    if (value && typeof value === "object") {
      if (seen.has(value)) return;
      seen.add(value);

      yield value;

      for (const p in value) {
        if (hasOwn.call(value, p)) {
          yield* iterate(value[p as never]);
        }
      }
    } else {
      yield value;
    }
  }

  yield* iterate(value);
}

/** Copies an object tree recursively. Recursive object structures are maintained. */
export function deepClone<T>(value: T): T {
  const map = new Map<object, object>();

  const copyObjectType = (value: object): object | null => {
    if (Array.isArray(value)) return [];
    if (value instanceof RegExp) return new RegExp(value.source, value.flags);
    if (value instanceof Date) return new Date(value.getTime());
    const TypedArray = TypedArrays.find((Arr) => value instanceof Arr);
    if (TypedArray) {
      return (value as InstanceType<(typeof TypedArrays)[number]>).slice();
    }
    if (value instanceof ArrayBuffer) {
      return value.slice();
    }
    if (value instanceof DataView) {
      return new DataView(value.buffer.slice());
    }
    if (value instanceof Set) {
      return new Set(iterate([...value]) as unknown[]);
    }
    if (value instanceof Map) {
      return new Map(iterate([...value]) as [unknown, unknown][]);
    }
    const proto = Object.getPrototypeOf(value);
    if (proto === null || proto === Object.prototype) return {};
    return null;
  };

  const iterate = (value: unknown): unknown => {
    if (value === null || typeof value !== "object") return value;

    let mapped: object | undefined = map.get(value);
    if (!mapped) {
      const preMapped = copyObjectType(value);
      if (preMapped === null) {
        map.set(value, value);
        return value; // return class objects as is: don't deep-clone
      }

      mapped = preMapped;
      Object.setPrototypeOf(mapped, Object.getPrototypeOf(value));

      map.set(value, mapped);

      const proto = Object.getPrototypeOf(mapped);
      if (
        Array.isArray(mapped) ||
        proto === null ||
        proto === Object.prototype
      ) {
        for (const prop of Object.getOwnPropertyNames(value) as never[]) {
          mapped[prop] = iterate(value[prop]) as never;
        }
        for (const symb of Object.getOwnPropertySymbols(value) as never[]) {
          mapped[symb] = iterate(value[symb]) as never;
        }
      }
    }

    return mapped;
  };

  return iterate(value) as T;
}

/** Seals an object and all descendant objects in its connected tree */
export function deepSeal<T>(value: T): T {
  for (const object of deepIterate(value)) {
    if (object !== null && typeof object === "object") {
      Object.seal(object);
    }
  }
  return value;
}

/** Freezes an object and all descendant objects in its connected tree */
export function deepFreeze<T>(value: T): T {
  for (const object of deepIterate(value)) {
    if (object !== null && typeof object === "object") {
      Object.freeze(object);
    }
  }
  return value;
}

/** Compare two values deeply. Return true if they are equal, containing identical contents. */
export const deepCompare = Object.assign(
  function (a: unknown, b: unknown): boolean {
    deepCompare.lastDifferencePath = null;

    const prefixPath = (pfx: string) => {
      deepCompare.lastDifferencePath =
        pfx +
        (deepCompare.lastDifferencePath
          ? "." + deepCompare.lastDifferencePath
          : "");
    };

    const seen = new Map<object, object[]>(); // maps an object to a list of objects that are equal

    const compareObjectsByType = (a: object, b: object): boolean | null => {
      if (a instanceof Date && b instanceof Date) {
        return a.getTime() === b.getTime();
      }
      if (a instanceof RegExp && b instanceof RegExp) {
        return a.source === b.source && a.flags === b.flags;
      }
      const ArrA = TypedArrays.find((Arr) => a instanceof Arr);
      const ArrB = TypedArrays.find((Arr) => b instanceof Arr);
      if (ArrA && ArrA === ArrB) {
        return (
          (a as number[]).length === (b as number[]).length &&
          (a as number[]).every((a, i) => a === (b as number[])[i])
        );
      }
      return null;
    };

    const iterate = (a: unknown, b: unknown): boolean => {
      if (typeof a !== typeof b) return false;
      if (typeof a !== "object") {
        if (typeof a === "number" && typeof b === "number") {
          return a === b || (isNaN(a) && isNaN(b));
        } else {
          return a === b;
        }
      }
      if (a === b) return true;
      if (!a || !b) return false;

      // at this point, a and b are both non-null objects

      let matching = seen.get(a);
      if (!matching) seen.set(a, (matching = []));

      if (matching.includes(b)) return true; // b was already tested against a
      matching.push(b); // b will have been tested against a

      const compared = compareObjectsByType(a, b);
      if (compared !== null) return compared;

      const aKeys = Object.keys(a) as (keyof typeof a)[];
      const bKeys = Object.keys(b) as (keyof typeof b)[];
      if (aKeys.length !== bKeys.length) {
        prefixPath(arrayExclusive(aKeys, bKeys)[0]);
        return false;
      }

      for (const k of aKeys) {
        if (!bKeys.includes(k)) {
          prefixPath(k);
          return false;
        }
        if (!iterate(a[k], b[k])) {
          prefixPath(k);
          return false;
        }
      }

      return true;
    };

    const result = iterate(a, b);
    // if path is empty, use empty string vs. null
    if (!result) deepCompare.lastDifferencePath ??= "";
    return result;
  },
  { lastDifferencePath: null } as { lastDifferencePath: null | string }
);
